import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { fetchVote, upvoteCall, downvoteCall } from '../../services/bff'
import logger from '../../utils/logger'

/**
 * VotingHandler component to manage vote state and voting actions
 * This is a logical component with no UI elements - it manages the
 * voting state and provides handlers to its children via render props
 */
export const CallVotingHandlerController = ({
  userId,
  rowsList,
  children
}) => {
  const [voteStates, setVoteStates] = useState({})
  const [currentVote, setCurrentVote] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    const getVotes = async () => {
      const votes = {}

      await Promise.all(
        rowsList.map(async (row) => {
          const callId = row[3]
          if (!callId) return

          try {
            const data = await fetchVote(callId, userId)
            votes[callId] = data.vote === 1 ? 'up' : data.vote === -1 ? 'down' : null
          } catch (error) {
            votes[callId] = null
          }
        })
      )

      setVoteStates(votes)
    }

    if (rowsList?.length > 0 && userId) {
      getVotes()
    }
  }, [rowsList, userId])

  const handleRowClick = async (row) => {
    const callId = row[3]
    if (!callId) {
      logger.error('No CALL ID found in this row.')
      return
    }

    setSelectedRow({ ...row, id: callId })

    try {
      const data = await fetchVote(callId, userId)
      setCurrentVote(data.vote === 1 ? 'up' : data.vote === -1 ? 'down' : null)
    } catch (error) {
      logger.error('Error fetching vote for call', error)
      setCurrentVote(null)
    }

    setDrawerOpen(true)
  }

  const handleUpvote = async () => {
    if (!selectedRow || !selectedRow.id) {
      logger.error('No call ID available for upvote')
      return
    }

    const success = await upvoteCall(selectedRow.id, userId)

    if (success) {
      setCurrentVote('up')
      setVoteStates((prev) => ({ ...prev, [selectedRow.id]: 'up' }))
      return true
    } else {
      logger.error('Upvote failed')
      return false
    }
  }

  const handleDownvote = async () => {
    if (!selectedRow || !selectedRow.id) {
      logger.error('No call ID available for downvote')
      return
    }

    const success = await downvoteCall(selectedRow.id, userId)

    if (success) {
      setCurrentVote('down')
      setVoteStates((prev) => ({ ...prev, [selectedRow.id]: 'down' }))
      return true
    } else {
      logger.error('Downvote failed')
      return false
    }
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
  }

  return children({
    voteStates,
    currentVote,
    selectedRow,
    drawerOpen,
    onRowClick: handleRowClick,
    onUpvote: handleUpvote,
    onDownvote: handleDownvote,
    onCloseDrawer: handleCloseDrawer
  })
}

CallVotingHandlerController.propTypes = {
  userId: PropTypes.string,
  rowsList: PropTypes.array,
  children: PropTypes.func.isRequired
}

export default CallVotingHandlerController
